import { useMemo, useRef } from 'react';
import { format as formatDateFns } from 'date-fns';
import { MenuProps } from 'react-select';
import { Matcher } from 'react-day-picker';
import BaseSelect from 'lib/BaseSelect';
import DateRangePicker from 'lib/DatePicker/DateRangePicker';
import { CodeNameModel } from 'components/common/types';
import { DATE_DISPLAY_FORMAT } from 'constants/dateTimePicker';
import { useDateRangeSelectMenu } from './useDateRangeSelectMenu';

const DATE_RANGE_OPTION = { code: 'date-range', name: 'date-range' };

export interface DateRangeSelectProps {
  startDate: Date | undefined;
  endDate: Date | undefined;
  onStartDateChange: (date: Date | undefined) => void;
  onEndDateChange: (date: Date | undefined) => void;
  minDate: Date;
  maxDate: Date;
  isClearable?: boolean;
  placeholder?: string;
  dataTestId?: string;
  dataPendoId?: string;
  defaultMonth?: Date;
  shouldDefaultToDateAfterFromSelection?: boolean;
}

const DateRangeSelect = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  minDate,
  maxDate,
  isClearable = false,
  placeholder = 'Select date range',
  dataTestId = 'date-range-select',
  dataPendoId,
  defaultMonth,
  shouldDefaultToDateAfterFromSelection = false,
}: DateRangeSelectProps): React.ReactElement => {
  const hasValue = !!startDate && !!endDate;
  const { menuIsOpen, containerRef, handleMenuOpen, handleMenuClose, handleMenuMouseDown } = useDateRangeSelectMenu();

  const displayText = useMemo(() => {
    if (hasValue)
      return `${formatDateFns(startDate, DATE_DISPLAY_FORMAT)} - ${formatDateFns(endDate, DATE_DISPLAY_FORMAT)}`;
    return '';
  }, [startDate, endDate, hasValue]);

  const disabledDays: Matcher[] = useMemo(() => [{ before: minDate }, { after: maxDate }], [minDate, maxDate]);

  const selectedValue = hasValue ? { ...DATE_RANGE_OPTION, name: displayText } : null;

  const propsRef = useRef({
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    disabledDays,
    defaultMonth,
    shouldDefaultToDateAfterFromSelection,
  });
  propsRef.current = {
    startDate,
    endDate,
    onStartDateChange,
    onEndDateChange,
    disabledDays,
    defaultMonth,
    shouldDefaultToDateAfterFromSelection,
  };

  const customComponents = useMemo(
    () => ({
      Menu: ({ innerRef, innerProps, placement }: MenuProps) => {
        const { onMouseDown: menuOnMouseDown, ...restInnerProps } = innerProps;
        return (
          <div
            role="presentation"
            ref={innerRef}
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...restInnerProps}
            onMouseDown={(e: React.MouseEvent<HTMLDivElement>) => handleMenuMouseDown(e, menuOnMouseDown)}
            className="DayPickerInput-Overlay w-[350px]"
            style={{
              position: 'absolute',
              left: 0,
              zIndex: 50,
              ...(placement === 'top' ? { bottom: '100%', marginBottom: 4 } : { top: '100%', marginTop: 4 }),
            }}
          >
            <DateRangePicker
              from={propsRef.current.startDate ?? null}
              to={propsRef.current.endDate ?? null}
              setFrom={propsRef.current.onStartDateChange}
              setTo={propsRef.current.onEndDateChange}
              disabledDatePickerDays={propsRef.current.disabledDays}
              defaultMonth={propsRef.current.defaultMonth}
              isWeekNumbersVisible
              shouldDefaultToDateAfterFromSelection={propsRef.current.shouldDefaultToDateAfterFromSelection}
              width="w-full"
            />
          </div>
        );
      },
    }),
    [handleMenuMouseDown],
  );

  const handleClear = (_name: string, value: CodeNameModel | CodeNameModel[] | null): void => {
    if (!value) {
      onStartDateChange(undefined);
      onEndDateChange(undefined);
    }
  };

  return (
    <div ref={containerRef}>
      <BaseSelect
        name="dateRange"
        dataTestId={dataTestId}
        dataPendoId={dataPendoId}
        placeholder={placeholder}
        options={[DATE_RANGE_OPTION]}
        selectedValue={selectedValue}
        onSelect={handleClear}
        isClearable={isClearable}
        isSelectionVisible
        closeMenuOnSelect={false}
        withPortal={false}
        menuPlacement="auto"
        customComponents={customComponents}
        menuIsOpen={menuIsOpen}
        onMenuOpen={handleMenuOpen}
        onMenuClose={handleMenuClose}
      />
    </div>
  );
};

export default DateRangeSelect;
